import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Circle, Copy, Link2, Video, MapPin, Users, Upload, ExternalLink, Loader2 } from "lucide-react";
import { PartnerLeaderboard } from "@/components/PartnerLeaderboard";

const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

function Step({ n, title, done, children, testid }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {done ? <CheckCircle2 className="h-5 w-5 text-sam-green" /> : <Circle className="h-5 w-5 text-sam-slate" />}
        <span className="font-display font-bold text-sam-navy">Step {n}. {title}</span>
      </div>
      {children}
    </div>
  );
}

function LinkKit({ kit, save }) {
  return (
    <>
      <p className="text-xs text-sam-slate mb-2">Your four short links carry your partner code. Save them as phone shortcuts and put them in every bio.</p>
      <div className="space-y-1.5">
        {Object.entries(kit.links).map(([k, url]) => (
          <div key={k} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2" data-testid={`kit-link-${k}`}>
            <span className="sm:w-36 shrink-0 text-xs font-semibold text-sam-navy">{kit.labels[k]} <span className="sm:hidden font-normal text-[10px] text-sam-slate">· {kit.clicks[k] || 0} click{kit.clicks[k] === 1 ? "" : "s"}</span></span>
            <div className="flex flex-1 items-center gap-2 min-w-0">
              <Input readOnly value={url} className="h-8 text-xs font-mono min-w-0" />
              <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => copy(url)} data-testid={`kit-copy-${k}`}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
            <span className="hidden sm:inline w-16 text-[10px] text-sam-slate">{kit.clicks[k] || 0} click{kit.clicks[k] === 1 ? "" : "s"}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => copy(Object.entries(kit.links).map(([k, u]) => `${kit.labels[k]}: ${u}`).join("\n"))} data-testid="kit-copy-all"><Copy className="h-3.5 w-3.5 mr-1" />Copy all for a bio</Button>
        {!kit.steps.links && <Button size="sm" className="h-8 text-xs bg-sam-navy" onClick={() => save({ links_saved: true })} data-testid="kit-links-done">I saved my links</Button>}
      </div>
    </>
  );
}

function Videos({ kit, save }) {
  const [v, setV] = useState({ customer: kit.videos.customer || "", partner: kit.videos.partner || "" });
  const sc = kit.scripts;
  return (
    <>
      <p className="text-xs text-sam-slate mb-2">Record (a) your customer video from the approved Video Sales Letter script and (b) your partner opportunity video from the approved compensation plan. Put your link in the description, on screen and in the pinned comment.</p>
      {[["customer", "Customer video script", sc.customer_vsl, kit.links.plans], ["partner", "Partner opportunity video script", sc.partner_comp, kit.links.recruit]].map(([k, label, text, link]) => (
        <details key={k} className="mb-2 rounded-lg border border-border p-2" data-testid={`kit-script-${k}`}>
          <summary className="cursor-pointer text-xs font-semibold text-sam-navy break-all">{label} · link to use: <span className="font-mono">{link}</span></summary>
          <p className="mt-2 whitespace-pre-wrap text-xs text-sam-slate">{text || "Your approved script will appear here once Saving America Money adds it."}</p>
        </details>
      ))}
      <div className="grid sm:grid-cols-2 gap-2">
        <Input placeholder="Customer video URL" value={v.customer} onChange={(e) => setV({ ...v, customer: e.target.value })} className="h-8 text-xs" data-testid="kit-video-customer" />
        <Input placeholder="Partner video URL" value={v.partner} onChange={(e) => setV({ ...v, partner: e.target.value })} className="h-8 text-xs" data-testid="kit-video-partner" />
      </div>
      <Button size="sm" className="mt-2 h-8 text-xs bg-sam-navy" onClick={() => save({ videos: v })} data-testid="kit-videos-save"><Video className="h-3.5 w-3.5 mr-1" />Save video links</Button>
    </>
  );
}

function Territory({ kit, reload }) {
  const [z, setZ] = useState(["", "", ""]);
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  const check = async (i) => {
    const zip = z[i].trim();
    if (!/^\d{5}$/.test(zip)) return;
    try { const { data } = await api.get(`/my/territory-check?zip=${zip}`); setChecks((c) => ({ ...c, [i]: data })); }
    catch (e) { setChecks((c) => ({ ...c, [i]: { error: e?.response?.data?.detail || "Invalid ZIP" } })); }
  };
  const claim = async () => {
    setBusy(true);
    try { await api.post("/my/territory-claim", { zips: z.map((x) => x.trim()) }); toast.success("Territory claim sent for approval"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Claim failed"); }
    setBusy(false);
  };
  const t = kit.territory;
  return (
    <>
      <p className="text-xs text-sam-slate mb-2">Choose your home ZIP and two neighboring ZIPs. ZIPs already assigned to another partner can't be claimed. Saving America Money approves each claim.</p>
      {t.approved.length > 0 && <p className="text-xs mb-2" data-testid="kit-territory-approved">Your area ZIPs: <b className="font-mono text-sam-navy">{t.approved.join(", ")}</b></p>}
      {t.status === "pending" && <p className="text-xs mb-2 text-sam-amber font-semibold" data-testid="kit-territory-pending">Waiting for approval: {t.pending.join(", ")}</p>}
      {t.status === "rejected" && <p className="text-xs mb-2 text-sam-crimson" data-testid="kit-territory-rejected">Your last claim wasn't approved — try different ZIPs.</p>}
      {t.status !== "pending" && (
        <>
          <div className="grid sm:grid-cols-3 gap-2">
            {["Home ZIP", "Neighbor ZIP", "Neighbor ZIP"].map((label, i) => (
              <div key={label + i}>
                <Input placeholder={label} value={z[i]} inputMode="numeric" onChange={(e) => setZ(z.map((x, j) => (j === i ? e.target.value.replace(/\D/g, "").slice(0, 5) : x)))} onBlur={() => check(i)} className="h-8 text-xs" data-testid={`kit-zip-${i}`} />
                {checks[i] && <div className="mt-1 text-[11px] text-sam-slate" data-testid={`kit-zip-check-${i}`}>
                  {checks[i].error ? <span className="text-sam-crimson">{checks[i].error}</span> : <>
                    {checks[i].city}, {checks[i].state} · {checks[i].taken ? <span className="text-sam-crimson font-semibold">already assigned</span> : <span className="text-sam-green font-semibold">available</span>}
                    {checks[i].routes.length ? checks[i].routes.map((r) => <div key={r.route_code}>{r.route_code}: {r.open_spots} open spots{r.taken_categories.length ? ` · taken: ${r.taken_categories.join(", ")}` : " · all categories open"}</div>) : <div>No postcard route yet — you can start one.</div>}
                  </>}
                </div>}
              </div>
            ))}
          </div>
          <Button size="sm" className="mt-2 h-8 text-xs bg-sam-navy" disabled={busy || z.some((x) => x.length !== 5)} onClick={claim} data-testid="kit-territory-claim"><MapPin className="h-3.5 w-3.5 mr-1" />Claim these ZIPs</Button>
        </>
      )}
    </>
  );
}

function Prospects({ kit, reload }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { const { data } = await api.post("/my/prospects/import", { csv: await f.text() }); toast.success(`Imported ${data.imported} prospects${data.duplicates ? `, ${data.duplicates} duplicates skipped` : ""} (${data.total} total)`); reload(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Import failed"); }
    setBusy(false); e.target.value = "";
  };
  const pct = Math.min(100, kit.prospects);
  return (
    <>
      <p className="text-xs text-sam-slate mb-2">Build a list of 100 local owners and import it. CSV headers: name, business, phone, email, city, zip.</p>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden mb-1"><div className="h-full bg-sam-crimson" style={{ width: `${pct}%` }} /></div>
      <p className="text-xs font-semibold text-sam-navy mb-2" data-testid="kit-prospect-count">{kit.prospects} / 100 prospects</p>
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="kit-prospect-file" />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy} onClick={() => ref.current?.click()} data-testid="kit-prospect-upload">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}Upload CSV</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" asChild><Link to="/prospector" data-testid="kit-hub-link"><ExternalLink className="h-3.5 w-3.5 mr-1" />Open your hub</Link></Button>
      </div>
    </>
  );
}

function Profile({ kit, save }) {
  const [p, setP] = useState({ display_name: "", headline: "", bio: "", photo_url: "", city: "", ...kit.profile });
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid="kit-profile">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <span className="font-display font-bold text-sam-navy">Your public partner profile</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => copy(kit.profile_url)} data-testid="kit-profile-copy"><Link2 className="h-3.5 w-3.5 mr-1" />Copy profile link</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" asChild><a href={`/p/${kit.code}`} target="_blank" rel="noreferrer" data-testid="kit-profile-view"><ExternalLink className="h-3.5 w-3.5 mr-1" />View</a></Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input placeholder="Display name" value={p.display_name} onChange={(e) => setP({ ...p, display_name: e.target.value })} className="h-8 text-xs" data-testid="kit-profile-name" />
        <Input placeholder="Headline" value={p.headline} onChange={(e) => setP({ ...p, headline: e.target.value })} className="h-8 text-xs" data-testid="kit-profile-headline" />
        <Input placeholder="Photo URL" value={p.photo_url} onChange={(e) => setP({ ...p, photo_url: e.target.value })} className="h-8 text-xs" data-testid="kit-profile-photo" />
        <Input placeholder="City" value={p.city} onChange={(e) => setP({ ...p, city: e.target.value })} className="h-8 text-xs" data-testid="kit-profile-city" />
      </div>
      <Textarea placeholder="Short bio" value={p.bio} onChange={(e) => setP({ ...p, bio: e.target.value })} className="mt-2 text-xs min-h-[60px]" data-testid="kit-profile-bio" />
      <Button size="sm" className="mt-2 h-8 text-xs bg-sam-navy" onClick={() => save({ profile: p })} data-testid="kit-profile-save">Save profile</Button>
    </div>
  );
}

export function PartnerKit() {
  const [kit, setKit] = useState(null);
  const load = () => api.get("/my/partner-kit").then(({ data }) => setKit(data)).catch(() => setKit(false));
  useEffect(() => { load(); }, []);
  if (!kit) return null;
  const save = async (body) => {
    try { const { data } = await api.patch("/my/partner-kit", body); setKit(data); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const done = Object.values(kit.steps).filter(Boolean).length;
  return (
    <section className="mb-8 space-y-3" data-testid="partner-kit">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-xl font-black text-sam-navy flex items-center gap-2"><Users className="h-5 w-5 text-sam-crimson" />Your first steps</h2>
        <span className="text-xs font-semibold text-sam-slate" data-testid="partner-kit-progress">{done} of 4 done</span>
      </div>
      <PartnerLeaderboard />
      <Step n={1} title="Build your link kit" done={kit.steps.links} testid="kit-step-links"><LinkKit kit={kit} save={save} /></Step>
      <Step n={2} title="Record your two videos" done={kit.steps.videos} testid="kit-step-videos"><Videos kit={kit} save={save} /></Step>
      <Step n={3} title="Claim your territory" done={kit.steps.territory} testid="kit-step-territory"><Territory kit={kit} reload={load} /></Step>
      <Step n={4} title="Load your first 100 prospects" done={kit.steps.prospects} testid="kit-step-prospects"><Prospects kit={kit} reload={load} /></Step>
      <Profile kit={kit} save={save} />
    </section>
  );
}
